import * as TabsPrimitive from "@radix-ui/react-tabs";
import cn from "classnames";
import { motion } from "framer-motion";
import {
	type ComponentPropsWithoutRef,
	type ElementRef,
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";

const Tabs = TabsPrimitive.Root;

/**
 * Context to scope layoutId per TabsList so multiple tab groups on the
 * same page don't share the same sliding indicator.
 */
const TabsLayoutIdContext = createContext<string>("");

const TabsList = forwardRef<
	ElementRef<typeof TabsPrimitive.List>,
	ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
	const layoutId = useId();
	return (
		<TabsLayoutIdContext.Provider value={layoutId}>
			<TabsPrimitive.List
				ref={ref}
				className={cn(
					"inline-flex",
					"items-center",
					"justify-center",
					"text-secondary",
					"border-b",
					className
				)}
				{...props}
			/>
		</TabsLayoutIdContext.Provider>
	);
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = forwardRef<
	ElementRef<typeof TabsPrimitive.Trigger>,
	ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
	const layoutId = useContext(TabsLayoutIdContext);
	const innerRef = useRef<HTMLButtonElement | null>(null);
	const [isActive, setIsActive] = useState(false);

	// Observe the data-state attribute set by Radix to detect active tab
	useEffect(() => {
		const el = innerRef.current;
		if (!el) return;

		const observer = new MutationObserver(() => {
			setIsActive(el.getAttribute("data-state") === "active");
		});
		observer.observe(el, { attributes: true, attributeFilter: ["data-state"] });
		// Set initial state
		setIsActive(el.getAttribute("data-state") === "active");
		return () => observer.disconnect();
	}, []);

	// Merge forwarded ref with our internal ref
	const mergedRef = useCallback(
		(node: HTMLButtonElement | null) => {
			innerRef.current = node;
			if (typeof ref === "function") {
				ref(node);
			} else if (ref) {
				ref.current = node;
			}
		},
		[ref]
	);

	return (
		<TabsPrimitive.Trigger
			ref={mergedRef}
			className={cn(
				"relative",
				"inline-flex",
				"gap-2",
				"items-center",
				"justify-center",
				"whitespace-nowrap",
				"-mb-px",
				"px-4",
				"py-2",
				"h-8",
				"py-1.5",
				"text-sm",
				"font-medium",
				"ring-offset-background",
				"transition-colors",
				"duration-150",
				"focus-visible:outline-none",
				"focus-visible:ring-2",
				"focus-visible:ring-ring",
				"focus-visible:ring-offset-2",
				"disabled:pointer-events-none",
				"disabled:opacity-50",
				"data-[state=active]:text-primary",
				className
			)}
			{...props}
		>
			{children}
			{isActive && (
				<motion.span
					layoutId={`tab-indicator-${layoutId}`}
					className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand rounded-full"
					transition={{ type: "spring", stiffness: 500, damping: 30 }}
				/>
			)}
		</TabsPrimitive.Trigger>
	);
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = forwardRef<
	ElementRef<typeof TabsPrimitive.Content>,
	ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		tabIndex={-1}
		className={cn(
			"pt-4",
			"ring-offset-background",
			"focus-visible:outline-none",
			"focus-visible:ring-2",
			"focus-visible:ring-ring",
			"focus-visible:ring-offset-2",
			"data-[state=inactive]:hidden",
			className
		)}
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
